export function useCartApi(): CartApi {
  const cartStore = useCartStore();
  const cart = computed(() => cartStore.cart);
  const defaultPaginationSize = Number(useRuntimeConfig().public.defaultPaginationSize);
  const currency = useRuntimeConfig().public.currency as Currency;
  const locale = useRuntimeConfig().public.locale;
  const { ensureAuthenticated } = useDjustAuth();
  const {
    getCommercialOrder,
    getOrderShippingAddress,
    getOrderShippingType,
    getOrderBillingAddress,
  } = useCommercialOrder();
  const { updateCommercialOrderShippingInfo, updateCommercialOrderBillingInformation } =
    useCommercialOrder();
  const { getCustomerAccountAddresses } = useAccount();
  /**
   * Fetch a cart by ID
   */
  const getCartbyId = async (
    params: GetCartRequest,
    useStore = true
  ): Promise<GetCartbyIDResponse | null> => {
    try {
      await ensureAuthenticated();

      // Validate that cartId is provided
      const { cartId, currency } = params;
      if (!cartId) {
        throw new Error('The "cartId" parameter is required.');
      }

      // Call API to fetch the cart by ID
      const { data, error } = await useFetch<GetCartbyIDResponse>(`/api/carts/${cartId}`, {
        query: currency ? { currency } : undefined, // Include currency if provided
        key: `getcart-${cartId}`, // Unique key for caching
      });

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to retrieve cart:', error.value);
        throw new Error('Failed to retrieve cart.');
      }

      // Optionally update the store with the fetched cart
      if (useStore && data.value?.success) {
        cartStore.cart = data.value.cart;
      }

      // Return the cart response
      return data.value as GetCartbyIDResponse;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to retrieve cart:', error.message || error);
      throw new Error('Failed to retrieve cart. Please try again later.');
    }
  };

  /**
   * Retrieve all carts with filters
   */
  const getCarts = async (
    params: GetCartsRequest,
    useStore = true
  ): Promise<GetCartsResponse | null> => {
    try {
      await ensureAuthenticated();

      // Make an API call using useFetch
      const { data, error } = await useFetch<GetCartsResponse>('/api/carts', {
        params: {
          currency: params.currency,
          page: params.pageable.page,
          size: params.pageable.size,
          status: params.status ? JSON.stringify(params.status) : undefined,
          type: 'CART',
        },
        key: `getcarts-${JSON.stringify(params)}`, // Unique key for caching
      });

      // Check for errors
      if (error.value) {
        console.error('Failed to retrieve carts:', error.value);
        return null;
      }

      // Optionally update the store with the fetched carts
      if (useStore) {
        cartStore.carts = data.value?.carts || [];
      }
      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Unexpected error while retrieving carts:', error);
      return null;
    }
  };

  // Polling function to check cart status
  const pollCartStatus = async (cartId: string, interval = 2000, timeout = 30000) => {
    const startTime = Date.now();

    while (Date.now() - startTime < timeout) {
      const cart = (await getCartbyId({ cartId, currency }))?.cart;

      if (cart?.status === 'ON_GOING') {
        return cart; // Cart status is 'ON_GOING', exit polling
      }

      // Wait before trying again
      await new Promise((resolve) => setTimeout(resolve, interval));
    }

    throw new Error('Timeout: Cart status did not become ON_GOING');
  };

  // Setting order function from cart
  const setOrderFromCart = async (cart: Cart | GetCartResponse) => {
    if (cart?.status !== 'ON_GOING') {
      // Initialize order if cart status is not 'ON_GOING'
      const orderId = (await initializeOrdersFromCart(cart.id)) ?? '';
      // Poll until cart status is ON_GOING
      await pollCartStatus(cart.id);
      // Set addresses
      const addressesResp = (await getCustomerAccountAddresses()) as GetAccountAddressesResponse;
      if (addressesResp.success) {
        const shippingAddress = addressesResp?.addresses.filter(
          (address: Address) => address.shipping
        )?.[0];
        await updateCommercialOrderShippingInfo({
          orderId,
          shippingAddressId: shippingAddress.externalId,
          shippingType: 'STD',
        });
        const billingAddress = addressesResp?.addresses.filter(
          (address: Address) => address.billing
        )?.[0];
        await updateCommercialOrderBillingInformation({
          orderId,
          billingAddressId: billingAddress.externalId,
        });
      }
    }
  };

  const getCartOrCreate = async (
    params: GetCartsRequest,
    useStore = true,
    forceCreateNewCart = true
  ): Promise<Cart | CartWithTotals> => {
    try {
      await ensureAuthenticated();

      const savedCartId = localStorage.getItem('cartId');

      // 🔄 Recharge panier depuis localStorage
      if (savedCartId) {
        try {
          const response = await getCartbyId({ cartId: savedCartId });
          const restoredCart = response?.cart;
          if (restoredCart) {
            if (useStore) cartStore.cart = restoredCart;

            const lines = await getCartLines(
              {
                cartId: restoredCart.id,
                currency,
                pageable: { page: 0, size: defaultPaginationSize },
              },
              useStore
            );

            if (lines?.totalElements) {
              const fetchedOrder = (
                await getCommercialOrder({
                  orderId: restoredCart.id,
                  idType: 'CART_ID',
                  locale,
                })
              ).order;
              if (!fetchedOrder) await setOrderFromCart(restoredCart);
            }

            return restoredCart;
          }
        } catch {
          localStorage.removeItem('cartId');
        }
      }

      // 🔁 Sinon, récupère panier existant si non forcé
      if (!forceCreateNewCart) {
        const cartsResponse = await getCarts(params, useStore);
        const filteredCarts = cartsResponse?.carts;

        if (filteredCarts && filteredCarts.length > 0) {
          const firstCart = filteredCarts[0];
          if (useStore) cartStore.cart = firstCart;

          const data = await getCartLines(
            {
              cartId: firstCart.id,
              currency,
              pageable: { page: 0, size: defaultPaginationSize },
            },
            useStore
          );

          if (data?.totalElements) {
            const fetchedOrder = (
              await getCommercialOrder({
                orderId: firstCart.id,
                idType: 'CART_ID',
                locale,
              })
            ).order;
            if (!fetchedOrder) await setOrderFromCart(firstCart);
          }

          localStorage.setItem('cartId', firstCart.id);
          return firstCart;
        }
      }

      // 🆕 Création d’un nouveau panier
      const newCart = await createCart({ name: 'Default Cart' }, useStore);
      if (useStore && newCart?.success) {
        cartStore.cart = newCart.cart;
        cartStore.clearCartLines();
        localStorage.setItem('cartId', newCart.cart.id);
        return newCart.cart;
      } else {
        throw new Error('Failed to create a new cart.');
      }
    } catch (e) {
      console.error('Failed to get or create cart:', e);
      throw new Error('Failed to get or create cart. Please try again later.');
    }
  };

  /**
   * Create a new cart
   */
  const createCart = async (
    params: CreateCartRequest,
    useStore = true
  ): Promise<CreateCartResponse | null> => {
    try {
      await ensureAuthenticated();

      // Make an API call using useFetch
      const { data, error } = await useFetch<CreateCartResponse>('/api/carts/create', {
        method: 'POST',
        body: params,
        key: `createcart-${params.name}`, // Unique key for caching
      });

      // Check for errors
      if (error.value) {
        console.error('Failed to create cart:', error.value);
        return null;
      }

      // Optionally update the store with the created cart
      if (useStore) {
        cartStore.cart = data.value || null;
      }

      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Unexpected error while creating cart:', error);
      return null;
    }
  };

  /**
   * Update a cart by its ID
   */
  const updateCart = async (params: UpdateCartRequest): Promise<void> => {
    try {
      await ensureAuthenticated();

      // Validate parameters
      const { cartId, name, type } = params;
      if (!cartId || !name) {
        throw new Error('Both "cartId" and "name" parameters are required.');
      }

      // Call the API to update the cart
      const { data, error } = await useFetch(`/api/carts/${cartId}/update`, {
        method: 'PUT',
        body: { name, type },
        key: `updatecart-${cartId}`, // Unique key for caching
      });

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to update cart:', error.value);
        switch (error.value.statusCode) {
          case 400:
            throw new Error('Missing cart id or cart name already exists.');
          case 404:
            throw new Error('Cart not found.');
          default:
            throw new Error('Failed to update cart.');
        }
      }

      // Validate response format
      // if (data.value && typeof data.value === 'string') {
      //   return data.value;
      // } else {
      //   throw new Error('Unexpected response format.');
      // }
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to update cart:', error.message || error);
      throw new Error('An unexpected error occurred while updating the cart.');
    }
  };

  /**
   * Delete a cart by its ID
   */
  const deleteCart = async (
    cartId: string,
    useStore = true
  ): Promise<DeleteCartResponse | null> => {
    try {
      await ensureAuthenticated();

      // Call the API to delete the cart
      const { data, error } = await useFetch<DeleteCartResponse>(`/api/carts/${cartId}/delete`, {
        method: 'DELETE',
        key: `deletecart-${cartId}`, // Unique key for caching
      });

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to delete cart:', error.value);
        throw new Error('Failed to delete cart.');
      }

      // Optionally update the store
      if (data.value && useStore) {
        cartStore.cart = cartStore.cart?.id === cartId ? null : cartStore.cart;
        cartStore.carts = cartStore.carts.filter((cart) => cart.id !== cartId);
      }
      return data.value;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to delete cart:', error.message || error);
      throw new Error('An unexpected error occurred while deleting the cart.');
    }
  };

  /**
   * Initialize orders from cart
   */
  const initializeOrdersFromCart = async (cartId: string): Promise<string | null> => {
    try {
      await ensureAuthenticated();

      // Call the API to initialize orders from the cart
      const { data, error } = await useFetch<InitializeOrderResponse>(
        `/api/carts/${cartId}/initializeOrder`,
        {
          method: 'POST',
          key: `initializeOrder-${cartId}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to initialize orders from cart:', error.value);
        throw new Error('Failed to initialize orders from cart.');
      }

      return data.value?.orderId || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to initialize orders from cart:', error.message || error);
      return null;
    }
  };

  /**
   * Fetch cart lines by cart ID
   */
  const getCartLines = async (
    params: GetCartLinesRequest,
    useStore = true
  ): Promise<GetCartLinesResponse | null> => {
    try {
      await ensureAuthenticated();

      // Call API to fetch cart lines by cart ID
      const query = {
        currency: params.currency,
        page: params.pageable.page,
        size: params.pageable.size || defaultPaginationSize,
        supplierId: params.supplierId ? JSON.stringify(params.supplierId) : undefined,
        productVariantId: params.productVariantId
          ? JSON.stringify(params.productVariantId)
          : undefined,
        offerPriceId: params.offerPriceId ? JSON.stringify(params.offerPriceId) : undefined,
      };

      const { data, error } = await useFetch<GetCartLinesResponse>(
        `/api/carts/${params.cartId}/cartlines`,
        {
          query,
          key: `getcartlines-${params.cartId}-${JSON.stringify(query)}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to get cart lines:', error.value);
        throw new Error('Failed to get cart lines.');
      }

      // Optionally update the store with fetched cart lines
      if (useStore && data.value) {
        const fetchedLines = (data.value.cartLines as any[]) || [];
        const page = data.value.page ?? params.pageable.page ?? 0;
        const totalElements = data.value.totalElements ?? 0;

        if (params.append && page > 0) {
          const existingIds = new Set(
            cartStore.cartLines.map((line: any) => line.offer?.offerPriceId || line.id)
          );
          const uniqueNewLines = fetchedLines.filter(
            (line: any) => !existingIds.has(line.offer?.offerPriceId || line.id)
          );
          cartStore.setCartLines([...cartStore.cartLines, ...uniqueNewLines], {
            page,
            totalElements,
          });
        } else {
          cartStore.setCartLines(fetchedLines, { page, totalElements });
        }
      }

      // Return the response
      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Unexpected error while getting cart lines:', error.message || error);
      throw new Error('An unexpected error occurred while getting cart lines.');
    }
  };

  /**
   * Delete cart lines
   */
  const deleteCartLines = async (
    params: DeleteCartLinesRequest,
    useStore = true
  ): Promise<DeleteCartLinesResponse | null> => {
    try {
      await ensureAuthenticated();

      // Call the API to delete cart lines
      const { data, error } = await useFetch<DeleteCartLinesResponse>(
        `/api/carts/${params.cartId}/cartlines/delete`,
        {
          method: 'DELETE',
          body: params,
          key: `deleteCartLines-${Math.random()}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to delete cart lines:', error.value);
        throw new Error('Failed to delete cart lines.');
      }

      // Optionally update the store
      if (useStore) {
        await refreshCartStore(params.cartId, currency);
      }

      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to delete cart lines:', error.message || error);
      throw new Error('An unexpected error occurred while deleting cart lines.');
    }
  };

  /**
   * Clear cart by deleting it and creating a new one
   */
  const clearCart = async (cartId: string, useStore = true): Promise<void> => {
    try {
      await ensureAuthenticated();

      // Call the API to clear the cart
      const { data, error } = await useFetch<CreateCartResponse>(`/api/carts/${cartId}/clear`, {
        method: 'DELETE',
        key: `clearCart-${cartId}`, // Unique key for caching
      });

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to clear cart:', error.value);
        throw new Error('Failed to clear cart.');
      }

      // Optionally update the store with the new cart
      if (useStore && data.value) {
        cartStore.cart = data.value.cart;
        cartStore.clearCartLines();
      }
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to clear cart:', error.message || error);
      throw new Error('An unexpected error occurred while clearing the cart.');
    }
  };

  /**
   * Update cart lines
   */
  const updateCartLines = async (
    params: UpdateCartLinesRequest,
    useStore = true
  ): Promise<UpdateCartLinesResponse | null> => {
    try {
      await ensureAuthenticated();

      // Call the API to update cart lines
      const { data, error } = await useFetch<UpdateCartLinesResponse>(
        `/api/carts/${params.cartId}/updateCartLines`,
        {
          method: 'PUT',
          body: params,
          key: `updateCartLines-${params.cartId}`, // Unique key for caching
        }
      );

      if (data.value?.success) {
        const fetchedOrder = (
          await getCommercialOrder({
            orderId: cart.value.id,
            idType: 'CART_ID',
            locale,
          })
        ).order;
        if (!fetchedOrder) {
          await setOrderFromCart(cart.value);
        }
      }

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to update cart lines:', error.value);
        switch (error.value.statusCode) {
          case 400:
            throw new Error(
              'Quantity lower or equals to 0 / missing quantity / missing offerPriceId.'
            );
          case 404:
            throw new Error('Cart not found.');
          case 412:
            throw new Error('Impossible to add any cart line.');
          default:
            throw new Error('Failed to update cart lines.');
        }
      }

      // Optionally update the store
      if (useStore && data.value) {
        cartStore.cartLines = cartStore.cartLines.map(
          (line) =>
            params.linesToUpdate.find(
              (updatedLine) => updatedLine.offerPriceId === line.offer.offerPriceId
            ) || line
        );
      }

      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to update cart lines:', error.message || error);
      throw new Error('An unexpected error occurred while updating cart lines.');
    }
  };

  /**
   * Update cart lines by variant
   */
  const updateCartLinesByVariant = async (
    params: UpdateCartLinesByVariantRequest,
    useStore = true
  ): Promise<UpdateCartLinesResponse | null> => {
    try {
      await ensureAuthenticated();

      // Call the API to update cart lines by variant
      const { data, error } = await useFetch<UpdateCartLinesResponse>(
        `/api/carts/${params.cartId}/cartlines/update-by-variant`,
        {
          method: 'PUT',
          body: params,
          key: `updateCartLinesByVariant-${params.cartId}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to update cart lines by variant:', error.value);
        throw new Error('Failed to update cart lines by variant.');
      }

      // Optionally update the store
      if (useStore && data.value) {
        cartStore.cartLines = cartStore.cartLines.map(
          (line) =>
            params.linesToUpdate.find(
              (updatedLine) => updatedLine.productVariantId === line.productVariant?.id
            ) || line
        );
      }

      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to update cart lines by variant:', error.message || error);
      throw new Error('An unexpected error occurred while updating cart lines by variant.');
    }
  };

  /**
   * Add single or multiple products to the cart and refresh the store.
   */
  const addOrUpdateCartLine = async (
    params: AddOrUpdateCartLineRequest,
    useStore = true
  ): Promise<UpdateCartLinesResponse | null> => {
    try {
      await ensureAuthenticated();

      // Construct lines to update
      const lines = params.lines.map((line) => ({
        quantity: line.quantity,
        updateAction: line.updateAction,
        ...(params.typeId === 'offerPriceId'
          ? { offerPriceId: line.offerPriceId }
          : { productVariantId: line.variantId }),
      }));

      const requestParams = {
        cartId: params.cartId,
        typeId: params.typeId,
        lines,
      };

      // Call the API to add or update cart lines
      const { data, error } = await useFetch<UpdateCartLinesResponse>(
        `/api/carts/${params.cartId}/cartlines/add`,
        {
          method: 'PUT',
          body: requestParams,
          key: `addOrUpdateCartLine-${Math.random()}`, // Unique key for caching
        }
      );

      // Handle potential API errors
      if (error.value) {
        console.error('Failed to add or update cart lines:', error.value);
        throw new Error('Failed to add or update cart lines.');
      }

      // Optionally refresh the store
      if (useStore !== false) {
        await refreshCartStore(params.cartId, currency);
      }

      return data.value || null;
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to add or update cart lines:', error.message || error);
      throw new Error('An unexpected error occurred while adding or updating cart lines.');
    }
  };

  /**
   * Refresh the cart store with the latest data from the backend.
   */
  const refreshCartStore = async (cartId: string, currency: Currency): Promise<void> => {
    try {
      await ensureAuthenticated();

      // Reload every page the user already loaded, so an infinite scroll list is not truncated
      const loadedPage = cartStore.cartLinesPage ?? 0;
      const [cartResponse, pageResponses] = await Promise.all([
        getCartbyId({ cartId, currency }, false),
        Promise.all(
          Array.from({ length: loadedPage + 1 }, (_, page) =>
            getCartLines(
              {
                cartId,
                currency,
                pageable: { page, size: defaultPaginationSize },
              },
              false
            )
          )
        ),
      ]);

      // Validate responses
      if (
        !cartResponse ||
        pageResponses.some((response: GetCartLinesResponse | null) => !response)
      ) {
        throw new Error('Failed to fetch cart or cart lines.');
      }

      // Pages are fetched separately and the API does not guarantee a stable
      // order between them, so a line could otherwise land in the store twice
      const linesByKey = new Map<any, any>();
      pageResponses.forEach((response: GetCartLinesResponse | null) => {
        ((response?.cartLines as any[]) || []).forEach((line: any) => {
          linesByKey.set(line.offer?.offerPriceId || line.id, line);
        });
      });
      const lines = [...linesByKey.values()];
      // The first page always exists, unlike the last one once lines have been removed
      const total = pageResponses[0]?.totalElements ?? cartResponse.cart?.numberOfLines ?? 0;

      cartStore.cart = cartResponse.cart;
      cartStore.setCartLines(lines, {
        page: Math.min(loadedPage, Math.max(0, Math.ceil(total / defaultPaginationSize) - 1)),
        totalElements: total,
      });
    } catch (e) {
      const error = e as ApiError;
      console.error('Failed to refresh cart store:', error.message || error);
      throw new Error('An unexpected error occurred while refreshing the cart store.');
    }
  };
  return {
    getCartbyId,
    getCarts,
    createCart,
    updateCart,
    deleteCart,
    clearCart,
    getCartOrCreate,
    initializeOrdersFromCart,
    getCartLines,
    deleteCartLines,
    updateCartLines,
    updateCartLinesByVariant,
    addOrUpdateCartLine,
    refreshCartStore,
  };
}
